/**
 * KpiDashboardPanel — operator KPI / operations dashboard (admin-only).
 *
 * Business-intelligence view over existing Dispatch OS data. Polls
 * GET /api/admin/kpi/dashboard?range=… every 30s and renders:
 *   Row 1 — executive KPI cards
 *   Row 2 — revenue + bookings trend charts (recharts)
 *   Row 3 — live operations widgets ("right now")
 *   Row 4 — dispatch funnel
 *   Row 5 — revenue breakdown + ride-health summary
 *   Row 6 — booking-line calls (Twilio) + recent calls
 *
 * Read-only: no dispatch/lifecycle/payment mutations. Every interactive control
 * and critical readout carries a data-testid for the E2E suite.
 */
import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { useFinancialPrivacy } from "../../lib/financialPrivacy";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import {
  LayoutDashboard, RefreshCw, Car, Users, Wifi, AlertTriangle,
  PhoneCall, TrendingUp, Banknote, HeartPulse,
} from "lucide-react";

const POLL_MS = 30000;

const RANGES = [
  { id: "today",     label: "Today" },
  { id: "wtd",       label: "This week" },
  { id: "yesterday", label: "Yesterday" },
  { id: "7d",        label: "Last 7 days" },
  { id: "30d",       label: "Last 30 days" },
  { id: "mtd",       label: "This month" },
];

const gbp = (n) => `£${Number(n || 0).toFixed(2)}`;
const num = (n) => Number(n || 0).toLocaleString();

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d
    .toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
    .replace(",", "");
};

const REFUND_STATUS_CLS = {
  succeeded: "bg-[#DCFCE7] text-[#166534]",
  pending: "bg-[#FEF3C7] text-[#92400E]",
  failed: "bg-[#FEE2E2] text-[#991B1B]",
};

const fmtDuration = (sec) => {
  if (sec == null) return "—";
  const s = Math.round(sec);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
};

const fmtBucket = (b, granularity) =>
  granularity === "hour" ? (b || "").slice(11, 13) + ":00" : (b || "").slice(5);

const maskTail = (phone) => {
  if (!phone) return "Unknown";
  const t = String(phone);
  return t.length > 5 ? `${t.slice(0, 3)}•••${t.slice(-3)}` : t;
};


// ─── Small presentational helpers ──────────────────────────────────────────

const KpiCard = ({ label, value, sub, accent = "#0A0A0A", icon, testid }) => (
  <div className="bg-white border border-border p-4" data-testid={testid}>
    <div className="flex items-center justify-between">
      <div className="label-mini text-muted-foreground">{label}</div>
      {icon && <span style={{ color: accent }}>{icon}</span>}
    </div>
    <div className="font-heading text-3xl font-black mt-1" style={{ color: accent }} data-testid={`${testid}-value`}>
      {value}
    </div>
    {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
  </div>
);

const MiniMetric = ({ label, value, accent = "#0A0A0A", testid }) => (
  <div className="bg-white p-4" data-testid={testid}>
    <div className="label-mini text-muted-foreground">{label}</div>
    <div className="font-heading text-2xl font-black mt-1" style={{ color: accent }} data-testid={`${testid}-value`}>
      {value}
    </div>
  </div>
);

const SectionTitle = ({ icon, children }) => (
  <div className="flex items-center gap-2 mb-3">
    <span className="text-[#002FA7]">{icon}</span>
    <h3 className="font-heading text-lg font-black tracking-tight">{children}</h3>
  </div>
);


export function KpiDashboardPanel({ onOpenRide }) {
  // Display-only financial privacy — masks money figures without altering data.
  const { mask, masked } = useFinancialPrivacy();
  const [range, setRange] = useState("today");
  const [data, setData] = useState(null);
  const [refundDetails, setRefundDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState(null);

  const fetchData = useCallback(async (r) => {
    setRefreshing(true);
    try {
      const [res, refRes] = await Promise.all([
        api.get(`/admin/kpi/dashboard?range=${r}`),
        api.get(`/admin/refunds?range=${r}`).catch(() => ({ data: { refunds: [] } })),
      ]);
      setData(res.data);
      setRefundDetails(refRes?.data?.refunds || []);
      setErr(null);
    } catch (e) {
      setErr("Couldn't load dashboard data.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchData(range);
    const t = setInterval(() => fetchData(range), POLL_MS);
    return () => clearInterval(t);
  }, [range, fetchData]);

  if (loading && !data) {
    return (
      <div className="p-8 text-center text-muted-foreground" data-testid="kpi-loading">
        <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading dashboard…
      </div>
    );
  }
  if (err && !data) {
    return <div className="p-8 text-center text-[#991B1B]" data-testid="kpi-error">{err}</div>;
  }

  const ex = data.executive, rev = data.revenue, ops = data.operations;
  const fn = data.funnel, src = data.sources, rh = data.ride_health, bc = data.booking_calls;
  const refunds = data.refunds || {};
  const recurring = data.recurring || {};
  const trend = (data.trend || []).map((t) => ({ ...t, label: fmtBucket(t.bucket, data.granularity) }));

  return (
    <div className="space-y-6" data-testid="kpi-dashboard-panel">
      {/* Header + range selector */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <LayoutDashboard className="w-6 h-6 text-[#002FA7]" />
          <div>
            <h2 className="font-heading text-2xl font-black tracking-tight flex items-center gap-2">
              Operations Dashboard
              {refreshing && <RefreshCw className="w-4 h-4 animate-spin text-muted-foreground" data-testid="kpi-refreshing" />}
            </h2>
            <div className="text-xs text-muted-foreground">{data.range_label} · live counts update every 30s</div>
          </div>
        </div>
        <div className="flex border border-border" data-testid="kpi-range-selector">
          {RANGES.map((r) => (
            <button
              key={r.id}
              onClick={() => setRange(r.id)}
              disabled={refreshing}
              className={`px-3 py-2 text-xs font-bold uppercase tracking-wider transition-colors disabled:opacity-60 ${
                range === r.id ? "bg-[#0A0A0A] text-white" : "bg-white text-foreground hover:bg-secondary"
              }`}
              data-testid={`kpi-range-${r.id}`}
              aria-pressed={range === r.id}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* Row 1 — executive KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="kpi-executive-row">
        <KpiCard label="BOOKINGS" value={num(ex.bookings)} accent="#002FA7"
          icon={<TrendingUp className="w-4 h-4" />} testid="kpi-bookings" />
        <KpiCard label="COMPLETED RIDES" value={num(ex.completed)} accent="#10B981"
          icon={<Car className="w-4 h-4" />} testid="kpi-completed" />
        <KpiCard label="GROSS REVENUE" value={mask(gbp(ex.revenue))} accent="#0A0A0A"
          sub={`Net ${mask(gbp(rev.net))} · after refunds`}
          icon={<Banknote className="w-4 h-4" />} testid="kpi-revenue" />
        <KpiCard label="ACTIVE DRIVERS" value={num(ex.active_drivers)} accent="#7C3AED"
          icon={<Users className="w-4 h-4" />} testid="kpi-active-drivers" />
      </div>

      {/* Row 2 — trend charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="bg-white border border-border p-4" data-testid="kpi-revenue-chart">
          <SectionTitle icon={<Banknote className="w-4 h-4" />}>Revenue trend</SectionTitle>
          {masked ? (
            <div className="h-[220px] flex flex-col items-center justify-center text-center bg-secondary/30 border border-dashed border-border" data-testid="kpi-revenue-chart-masked">
              <Banknote className="w-6 h-6 text-muted-foreground mb-2" />
              <div className="font-heading font-black text-lg tracking-wide text-muted-foreground">••••••</div>
              <div className="text-xs text-muted-foreground mt-1">Revenue hidden · reveal to view trend</div>
            </div>
          ) : (
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={trend} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
              <defs>
                <linearGradient id="revFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#002FA7" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#002FA7" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEE" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} />
              <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
              <Tooltip formatter={(v) => gbp(v)} />
              <Area type="monotone" dataKey="revenue" stroke="#002FA7" strokeWidth={2} fill="url(#revFill)" />
            </AreaChart>
          </ResponsiveContainer>
          )}
        </div>
        <div className="bg-white border border-border p-4" data-testid="kpi-bookings-chart">
          <SectionTitle icon={<TrendingUp className="w-4 h-4" />}>Bookings trend</SectionTitle>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={trend} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEE" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} />
              <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="bookings" fill="#10B981" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Row 3 — live operations */}
      <div className="bg-white border border-border p-4" data-testid="kpi-operations">
        <SectionTitle icon={<Wifi className="w-4 h-4" />}>Live operations · right now</SectionTitle>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-border border border-border">
          <MiniMetric label="ACTIVE TRIPS" value={num(ops.active_trips)} accent="#002FA7" testid="ops-active-trips" />
          <MiniMetric label="DRIVERS ONLINE" value={num(ops.drivers_online)} accent="#10B981" testid="ops-drivers-online" />
          <MiniMetric label="DRIVERS BUSY" value={num(ops.drivers_busy)} accent="#F59E0B" testid="ops-drivers-busy" />
          <MiniMetric label="UNASSIGNED JOBS" value={num(ops.unassigned_jobs)}
            accent={ops.unassigned_jobs > 0 ? "#EF4444" : "#0A0A0A"} testid="ops-unassigned" />
          <MiniMetric label="AVG PICKUP" value={ops.avg_pickup_time_sec == null ? "—" : fmtDuration(ops.avg_pickup_time_sec)}
            accent="#0A0A0A" testid="ops-avg-pickup" />
        </div>
      </div>

      {/* Row 4 — dispatch funnel */}
      <div className="bg-white border border-border p-4" data-testid="kpi-funnel">
        <SectionTitle icon={<Car className="w-4 h-4" />}>Dispatch funnel</SectionTitle>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-px bg-border border border-border">
          <MiniMetric label="OFFERED" value={num(fn.offered)} accent="#002FA7" testid="funnel-offered" />
          <MiniMetric label="ACCEPTED" value={num(fn.accepted)} accent="#10B981" testid="funnel-accepted" />
          <MiniMetric label="REJECTED" value={num(fn.rejected)} accent="#F59E0B" testid="funnel-rejected" />
          <MiniMetric label="EXPIRED" value={num(fn.expired)} accent="#EF4444" testid="funnel-expired" />
          <MiniMetric label="COMPLETED" value={num(fn.completed)} accent="#10B981" testid="funnel-completed" />
          <MiniMetric label="AVG ACCEPT" value={fn.avg_accept_time_sec == null ? "—" : fmtDuration(fn.avg_accept_time_sec)}
            accent="#0A0A0A" testid="funnel-avg-accept" />
        </div>
      </div>

      {/* Row 4b — refunds (Refunds Phase 2) */}
      <div className="bg-white border border-border p-4" data-testid="kpi-refunds">
        <SectionTitle icon={<Banknote className="w-4 h-4" />}>Refunds · {data.range_label}</SectionTitle>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-border border border-border">
          <MiniMetric label="TOTAL REFUNDED" value={mask(gbp(refunds.total))} accent="#EF4444" testid="refunds-total" />
          <MiniMetric label="REFUND COUNT" value={num(refunds.count)} accent="#0A0A0A" testid="refunds-count" />
          <MiniMetric label="CARD" value={mask(gbp(refunds.card))} accent="#002FA7" testid="refunds-card" />
          <MiniMetric label="CASH" value={mask(gbp(refunds.cash))} accent="#F59E0B" testid="refunds-cash" />
          <MiniMetric label="ACCOUNT" value={mask(gbp(refunds.account))} accent="#7C3AED" testid="refunds-account" />
        </div>

        {/* Refunds Phase 3 — top refund reasons */}
        <div className="mt-3" data-testid="refunds-by-reason">
          <div className="label-mini text-muted-foreground mb-1.5">TOP REFUND REASONS</div>
          {(refunds.by_reason || []).length === 0 ? (
            <p className="text-xs text-muted-foreground italic" data-testid="refunds-by-reason-empty">No refunds in this period.</p>
          ) : (
            <div className="space-y-1.5">
              {refunds.by_reason.map((r, i) => {
                const max = refunds.by_reason[0]?.count || 1;
                const pct = Math.max(6, Math.round((r.count / max) * 100));
                return (
                  <div key={i} className="flex items-center gap-2 text-xs" data-testid={`refund-reason-row-${i}`}>
                    <div className="w-1/2 md:w-2/5 truncate" title={r.reason}>{r.reason}</div>
                    <div className="flex-1 bg-secondary h-4 relative overflow-hidden">
                      <div className="h-full bg-[#EF4444]/80" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="w-24 text-right tabular-nums text-muted-foreground">
                      <span className="font-semibold text-foreground">{num(r.count)}</span> · {mask(gbp(r.total))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Refunds Phase 3 — per-refund drill-down table (reporting only;
            never mutates ride fares or refund-processing logic). */}
        <div className="mt-4" data-testid="refunds-details">
          <div className="label-mini text-muted-foreground mb-1.5">
            REFUND DETAILS · {data.range_label}
          </div>
          {refundDetails.length === 0 ? (
            <p className="text-xs text-muted-foreground italic" data-testid="refunds-details-empty">
              No refunds in this period.
            </p>
          ) : (
            <div className="overflow-x-auto border border-border">
              <table className="w-full text-xs">
                <thead className="bg-secondary/40 text-left label-mini text-muted-foreground">
                  <tr>
                    <th className="px-2.5 py-2 font-bold">DATE/TIME</th>
                    <th className="px-2.5 py-2 font-bold">RIDE</th>
                    <th className="px-2.5 py-2 font-bold">PASSENGER</th>
                    <th className="px-2.5 py-2 font-bold">PAY</th>
                    <th className="px-2.5 py-2 font-bold text-right">AMOUNT</th>
                    <th className="px-2.5 py-2 font-bold">REASON / NOTE</th>
                    <th className="px-2.5 py-2 font-bold">BY ADMIN</th>
                    <th className="px-2.5 py-2 font-bold">STATUS</th>
                    <th className="px-2.5 py-2 font-bold">STRIPE REF</th>
                  </tr>
                </thead>
                <tbody>
                  {refundDetails.map((rf) => (
                    <tr key={rf.id} className="border-t border-border hover:bg-secondary/20" data-testid={`refund-detail-row-${rf.id}`}>
                      <td className="px-2.5 py-2 whitespace-nowrap text-muted-foreground font-mono-tab">{fmtDateTime(rf.created_at)}</td>
                      <td className="px-2.5 py-2 whitespace-nowrap">
                        {onOpenRide ? (
                          <button
                            type="button"
                            onClick={() => onOpenRide(rf.ride_id)}
                            className="font-mono-tab text-[#002FA7] hover:underline"
                            data-testid={`refund-ride-link-${rf.id}`}
                            title="Find this ride in the Rides tab"
                          >
                            {(rf.ride_id || "").substring(0, 8)}
                          </button>
                        ) : (
                          <span className="font-mono-tab text-muted-foreground">{(rf.ride_id || "").substring(0, 8)}</span>
                        )}
                      </td>
                      <td className="px-2.5 py-2 whitespace-nowrap">{rf.passenger_name || "—"}</td>
                      <td className="px-2.5 py-2 uppercase">{rf.payment_method || "—"}</td>
                      <td className="px-2.5 py-2 text-right font-bold text-[#EF4444] whitespace-nowrap" data-testid={`refund-detail-amount-${rf.id}`}>{mask(gbp(rf.amount))}</td>
                      <td className="px-2.5 py-2 max-w-[220px]">
                        <span className="block truncate" title={rf.reason}>{rf.reason || "—"}</span>
                        {rf.status === "failed" && rf.error && (
                          <span className="block text-[10px] text-[#991B1B] truncate" title={rf.error}>{rf.error}</span>
                        )}
                      </td>
                      <td className="px-2.5 py-2 whitespace-nowrap">{rf.admin_name || "—"}</td>
                      <td className="px-2.5 py-2">
                        <span className={`px-1.5 py-0.5 font-bold ${REFUND_STATUS_CLS[rf.status] || "bg-secondary text-muted-foreground"}`}>
                          {rf.status || "—"}
                        </span>
                      </td>
                      <td className="px-2.5 py-2 font-mono-tab text-[10px] text-muted-foreground">
                        {rf.stripe_refund_id
                          ? <span title={rf.stripe_refund_id}>{rf.stripe_refund_id}</span>
                          : <span className="opacity-50">{rf.payment_method === "cash" ? "cash — n/a" : rf.payment_method === "account" ? "ledger credit" : "—"}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      <div className="bg-white border border-border p-4" data-testid="kpi-recurring">
        <SectionTitle icon={<TrendingUp className="w-4 h-4" />}>Recurring revenue · MRR</SectionTitle>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-border border border-border">
          <MiniMetric label="TOTAL MRR" value={mask(gbp(recurring.mrr))} accent="#16A34A" testid="recurring-mrr" />
          <MiniMetric label="DRIVER MRR" value={mask(gbp(recurring.driver_mrr))} accent="#002FA7" testid="recurring-driver-mrr" />
          <MiniMetric label="CORPORATE MRR" value={mask(gbp(recurring.corporate_mrr))} accent="#0A0A0A" testid="recurring-corporate-mrr" />
          <MiniMetric label="ACTIVE SUBS" value={num(recurring.active)} accent="#16A34A" testid="recurring-active" />
          <MiniMetric label="PAST DUE" value={num(recurring.past_due)} accent="#F59E0B" testid="recurring-past-due" />
        </div>
        <p className="text-[11px] text-muted-foreground mt-2">
          Current snapshot. MRR normalises weekly plans to a monthly figure ·
          {" "}{num(recurring.driver_active)} driver + {num(recurring.corporate_active)} corporate active.
        </p>
      </div>

      {/* Row 5 — revenue breakdown + ride health + sources */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="bg-white border border-border p-4" data-testid="kpi-revenue-breakdown">
          <SectionTitle icon={<Banknote className="w-4 h-4" />}>Revenue breakdown</SectionTitle>
          <div className="space-y-2 text-sm">
            {[
              ["Gross revenue", mask(gbp(rev.gross)), "font-black"],
              ["Card", mask(gbp(rev.card))],
              ["Cash", mask(gbp(rev.cash))],
              ["Account", mask(gbp(rev.account))],
              ["Firm fees collected", mask(gbp(rev.firm_fees_collected))],
            ].map(([k, v, cls]) => (
              <div key={k} className="flex justify-between font-mono-tab">
                <span className="text-muted-foreground">{k}</span>
                <span className={cls || "font-bold"}>{v}</span>
              </div>
            ))}
            {/* Net Revenue (reporting only): Gross − Refunds issued. */}
            <div className="flex justify-between font-mono-tab" data-testid="kpi-refunds-issued">
              <span className="text-[#EF4444]">Refunds issued</span>
              <span className="font-bold text-[#EF4444]" data-testid="kpi-refunds-issued-value">
                {masked ? mask(gbp(rev.refunds_issued)) : `−${gbp(rev.refunds_issued)}`}
              </span>
            </div>
            <div className="flex justify-between font-mono-tab border-t border-border pt-2" data-testid="kpi-net-revenue">
              <span className="font-black text-[#166534]">Net revenue</span>
              <span className="font-black text-[#166534]" data-testid="kpi-net-revenue-value">{mask(gbp(rev.net))}</span>
            </div>
            <div className="flex justify-between font-mono-tab border-t border-border pt-2">
              <span className="text-[#92400E]">Outstanding cash due</span>
              <span className="font-black text-[#92400E]" data-testid="kpi-outstanding-cash">{mask(gbp(rev.outstanding_cash_due))}</span>
            </div>
          </div>
        </div>

        <div className="bg-white border border-border p-4" data-testid="kpi-sources">
          <SectionTitle icon={<Users className="w-4 h-4" />}>Booking sources</SectionTitle>
          <div className="space-y-2 text-sm">
            {[
              ["Passenger app", src.app],
              ["Admin / phone", src.dispatch_phone],
              ["WhatsApp (future)", src.whatsapp],
              ["Corporate / account", src.corporate],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between font-mono-tab">
                <span className="text-muted-foreground">{k}</span>
                <span className="font-bold">{num(v)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-border p-4" data-testid="kpi-ride-health">
          <SectionTitle icon={<HeartPulse className="w-4 h-4" />}>Ride health · 24h</SectionTitle>
          <div className="space-y-2 text-sm">
            {[
              ["Stuck rides", rh.stuck_rides, rh.stuck_rides > 0],
              ["Orphan recoveries", rh.orphan_recoveries_24h, false],
              ["Auto-offlined drivers", rh.auto_offlined_24h, rh.auto_offlined_24h > 0],
              ["Disconnect storms", rh.disconnect_storms_24h, rh.disconnect_storms_24h > 0],
            ].map(([k, v, warn]) => (
              <div key={k} className="flex justify-between font-mono-tab">
                <span className="text-muted-foreground flex items-center gap-1">
                  {warn && <AlertTriangle className="w-3 h-3 text-[#EF4444]" />} {k}
                </span>
                <span className={`font-bold ${warn ? "text-[#EF4444]" : ""}`}>{num(v)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Row 6 — booking-line calls */}
      <div className="bg-white border border-border p-4" data-testid="kpi-booking-calls">
        <SectionTitle icon={<PhoneCall className="w-4 h-4" />}>Booking-line calls (+44 1256 438389)</SectionTitle>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-border border border-border mb-3">
          <MiniMetric label="CALLS" value={num(bc.total)} accent="#002FA7" testid="calls-total" />
          <MiniMetric label="ANSWERED" value={num(bc.answered)} accent="#10B981" testid="calls-answered" />
          <MiniMetric label="MISSED" value={num(bc.missed)} accent={bc.missed > 0 ? "#EF4444" : "#0A0A0A"} testid="calls-missed" />
          <MiniMetric label="FALLBACK MSG" value={num(bc.fallback)} accent="#F59E0B" testid="calls-fallback" />
          <MiniMetric label="AVG DURATION" value={fmtDuration(bc.avg_duration_sec)} accent="#0A0A0A" testid="calls-avg-duration" />
        </div>
        <div className="label-mini text-muted-foreground mb-2">RECENT CALLS</div>
        {(!bc.recent || bc.recent.length === 0) ? (
          <div className="text-sm text-muted-foreground italic" data-testid="calls-empty">No booking calls in this range yet.</div>
        ) : (
          <div className="space-y-1" data-testid="calls-recent-list">
            {bc.recent.map((c, i) => (
              <div key={`${c.ts}-${c.from}-${i}`} className="flex items-center justify-between border border-border px-3 py-2 text-xs" data-testid="calls-recent-row">
                <span className="font-mono-tab text-muted-foreground">{maskTail(c.from)}</span>
                <div className="flex items-center gap-3">
                  <span className={`font-bold uppercase ${
                    c.answered ? "text-[#10B981]"
                    : c.status === "no_dispatcher" ? "text-[#92400E]"
                    : c.dial_status ? "text-[#EF4444]" : "text-muted-foreground"
                  }`}>
                    {c.answered ? "Answered"
                      : c.status === "no_dispatcher" ? "Fallback msg"
                      : c.dial_status ? (c.dial_status || "missed") : "Forwarded"}
                  </span>
                  <span className="font-mono-tab">{c.duration_sec ? fmtDuration(c.duration_sec) : "—"}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default KpiDashboardPanel;
